import { createAPIRequest, createAPIResponse, HTTPError, ListResponse, ResponseWrapper } from '@tlx/astro-shared';

export const MethodType = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
} as const;

export const callApi = async <T>(
    url: string,
    methodType: keyof typeof MethodType = MethodType.GET,
    payload?: string | FormData,
    additionalHeaders?: Record<string, string>,
) => {
    const init: RequestInit = {
        method: methodType,
        ...(payload && { body: payload }),
        headers: {
            'Content-Type': 'application/json; charset=utf-8',
            ...additionalHeaders,
        },
    };

    const request = createAPIRequest(url, init);
    const response = await fetch(request);
    let awaitedResponse;
    try {
        awaitedResponse = await createAPIResponse<T>(request, response);
    } catch (error) {
        if (error instanceof HTTPError) {
            return {
                response: undefined,
                error,
            };
        }
    }
    return {
        response: awaitedResponse,
        error: undefined,
    };
};

export const mapObjectToUrlSearchParams = (obj: Record<string, string>) => {
    const params = new URLSearchParams();
    Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (!(value === undefined || value === null)) {
            params.append(key, value);
        }
    });
    return params;
};

export const computeUrl = (url: string, searchParams?: URLSearchParams) => {
    if (searchParams === undefined) {
        return url;
    }
    return `${url}?${searchParams}`;
};

export const isResponseWrapper = <T>(
    response: ListResponse<T> | ResponseWrapper<T>,
): response is ResponseWrapper<T> => {
    return (response as ResponseWrapper<T>).value !== undefined;
};

export async function postFetcher<T = unknown, D = unknown>(url: string | URL, data: D): Promise<T> {
    const request = createAPIRequest(url.toString(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
    const response = await window.fetch(request);

    return createAPIResponse(request, response);
}
